import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Megaphone, FileText, Download, Play, Pause, FastForward, Rewind } from 'lucide-react';

/**
 * AnnouncementRibbon - Displays teacher announcements in a scrollable ribbon
 * Used on Landing Page and Course Page
 * Supports multiple attachment types: images, PDFs, audio, video
 * Features: Auto-scroll marquee with manual controls
 */
const AnnouncementRibbon = ({ announcements = [] }) => {
  const scrollRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(1); // 0.5x, 1x, 2x
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);

  // Auto-scroll animation
  const autoScroll = useCallback((timestamp) => {
    if (!scrollRef.current || !isAutoScrolling || isPaused) {
      animationRef.current = requestAnimationFrame(autoScroll);
      return;
    }

    if (!lastTimeRef.current) {
      lastTimeRef.current = timestamp;
    }

    const elapsed = timestamp - lastTimeRef.current;
    
    // Scroll 30-60 pixels per second based on speed
    if (elapsed > 16) { // ~60fps
      const scrollAmount = (30 * scrollSpeed * elapsed) / 1000;
      const container = scrollRef.current;
      
      // Check if we've reached the end
      const maxScroll = container.scrollWidth - container.clientWidth;
      
      if (container.scrollLeft >= maxScroll - 1) {
        // Reset to beginning smoothly
        container.scrollLeft = 0;
      } else {
        container.scrollLeft += scrollAmount;
      }
      
      lastTimeRef.current = timestamp;
    }

    animationRef.current = requestAnimationFrame(autoScroll);
  }, [isAutoScrolling, isPaused, scrollSpeed]);

  // Start/stop auto-scroll
  useEffect(() => {
    if (isAutoScrolling && announcements.length > 3) {
      animationRef.current = requestAnimationFrame(autoScroll);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAutoScrolling, autoScroll, announcements.length]);

  // Manual scroll function
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Pause on hover
  const handleMouseEnter = () => {
    if (isAutoScrolling) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (isAutoScrolling) {
      setIsPaused(false);
      lastTimeRef.current = null; // Reset timing to prevent jump
    }
  };

  // Toggle auto-scroll
  const toggleAutoScroll = () => {
    setIsAutoScrolling(!isAutoScrolling);
    if (!isAutoScrolling) {
      lastTimeRef.current = null;
    }
  };

  // Cycle through speeds: 0.5x -> 1x -> 2x -> 0.5x
  const cycleSpeed = () => {
    setScrollSpeed(prev => {
      if (prev === 0.5) return 1;
      if (prev === 1) return 2;
      return 0.5;
    });
  };

  if (!announcements || announcements.length === 0) {
    return null;
  }

  return (
    <section className="py-8 px-4" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-full"
              style={{ backgroundColor: '#163329' }}
            >
              <Megaphone className="h-5 w-5 text-white" />
            </div>
            <h2 
              className="text-2xl font-semibold"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              Media / Brochures / Announcements
            </h2>
          </div>
          
          {/* Navigation & Auto-scroll Controls */}
          {announcements.length > 3 && (
            <div className="hidden md:flex items-center gap-2">
              {/* Speed control */}
              <button
                onClick={cycleSpeed}
                className="px-2 py-1 rounded-full border text-xs font-medium hover:bg-gray-100 transition-colors"
                style={{ borderColor: '#163329', color: '#163329', minWidth: '44px' }}
                aria-label="Change scroll speed"
                title={`Speed: ${scrollSpeed}x (click to change)`}
              >
                {scrollSpeed}x
              </button>
              
              {/* Auto-scroll toggle */}
              <span className="text-xs mr-2 hidden lg:inline" style={{ color: '#5C635A' }}>
                Press this button to pause
              </span>
              <button
                onClick={toggleAutoScroll}
                className={`p-2 rounded-full border transition-colors ${
                  isAutoScrolling ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-100'
                }`}
                style={{ borderColor: isAutoScrolling ? '#22c55e' : '#163329' }}
                aria-label={isAutoScrolling ? 'Pause auto-scroll' : 'Start auto-scroll'}
                title={isAutoScrolling ? 'Auto-scrolling (click to stop)' : 'Click to start auto-scroll'}
              >
                {isAutoScrolling ? (
                  <Pause className="h-4 w-4" style={{ color: '#22c55e' }} />
                ) : (
                  <Play className="h-4 w-4" style={{ color: '#163329' }} />
                )}
              </button>
              
              {/* Manual navigation */}
              <button
                onClick={() => scroll('left')}
                className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
                style={{ borderColor: '#163329' }}
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-5 w-5" style={{ color: '#163329' }} />
              </button>
              <button
                onClick={() => scroll('right')}
                className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
                style={{ borderColor: '#163329' }}
                aria-label="Scroll right"
              >
                <ChevronRight className="h-5 w-5" style={{ color: '#163329' }} />
              </button>
            </div>
          )}
        </div>

        {/* Scrollable Ribbon with auto-scroll */}
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
          style={{ 
            scrollSnapType: isAutoScrolling ? 'none' : 'x mandatory',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {announcements.map((announcement) => (
            <AnnouncementCard key={announcement.id} announcement={announcement} />
          ))}
        </div>

        {/* Mobile controls */}
        <div className="flex items-center justify-center gap-2 mt-2 md:hidden">
          <span className="text-xs" style={{ color: '#5C635A' }}>
            Press this button to pause
          </span>
          <button
            onClick={toggleAutoScroll}
            className={`flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
              isAutoScrolling ? 'bg-green-50' : ''
            }`}
            style={{ borderColor: isAutoScrolling ? '#22c55e' : '#163329', color: isAutoScrolling ? '#22c55e' : '#163329' }}
          >
            {isAutoScrolling ? (
              <>
                <Pause className="h-3 w-3" /> Auto
              </>
            ) : (
              <>
                <Play className="h-3 w-3" /> Auto
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

/**
 * Render attachment based on type
 */
const AttachmentRenderer = ({ attachment }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const handleAudioToggle = (e) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoToggle = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  switch (attachment.type) {
    case 'image':
      return (
        <img
          src={attachment.url}
          alt={attachment.filename}
          className="w-full h-full object-cover object-top"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    
    case 'pdf':
      return (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="w-full h-full flex flex-col items-center justify-center bg-red-50 hover:bg-red-100 transition-colors"
        >
          <FileText className="h-10 w-10 text-red-500 mb-2" />
          <span className="text-xs text-red-700 font-medium px-2 text-center line-clamp-2">
            {attachment.filename}
          </span>
          <span className="flex items-center gap-1 text-xs text-red-600 mt-1">
            <Download className="h-3 w-3" /> Download PDF
          </span>
        </a>
      );
    
    case 'audio':
      return (
        <div 
          className="w-full h-full flex flex-col items-center justify-center bg-purple-50 p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleAudioToggle}
            className="w-12 h-12 rounded-full bg-purple-500 hover:bg-purple-600 flex items-center justify-center text-white mb-2 transition-colors"
          >
            {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
          </button>
          <span className="text-xs text-purple-700 font-medium text-center line-clamp-1 w-full">
            {attachment.filename}
          </span>
          <audio
            ref={audioRef}
            src={attachment.url}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />
        </div>
      );
    
    case 'video':
      return (
        <div 
          className="w-full h-full relative bg-black"
          onClick={(e) => e.stopPropagation()}
        >
          <video
            ref={videoRef}
            src={attachment.url}
            className="w-full h-full object-contain"
            onEnded={() => setIsPlaying(false)}
            onClick={handleVideoToggle}
          />
          {!isPlaying && (
            <button
              onClick={handleVideoToggle}
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
            >
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="h-7 w-7 text-gray-800 ml-1" />
              </div>
            </button>
          )}
        </div>
      );
    
    default:
      return null;
  }
};

/**
 * Individual Announcement Card
 */
const AnnouncementCard = ({ announcement }) => {
  const navigate = useNavigate();
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);
  
  // Get all attachments (including legacy image_url)
  // Use presigned URLs when available
  const attachments = (announcement.attachments || []).map(att => ({
    ...att,
    url: att.presigned_url || att.url  // Prefer presigned URL
  }));
  const hasLegacyImage = announcement.image_url && attachments.length === 0;
  
  const allAttachments = hasLegacyImage 
    ? [{ 
        url: announcement.presigned_image_url || announcement.image_url, 
        type: 'image', 
        filename: 'Image' 
      }]
    : attachments;

  // Check if this is an image-type announcement (has images but no PDFs as primary content)
  const hasImageAttachments = allAttachments.some(att => att.type === 'image');
  const hasPdfAttachments = allAttachments.some(att => att.type === 'pdf');

  const handleClick = () => {
    // If there's a link_url, open it in a new tab
    if (announcement.link_url) {
      window.open(announcement.link_url, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // For image-type announcements without a link, navigate to teacher gallery
    if (hasImageAttachments && !hasPdfAttachments && announcement.teacher_id) {
      navigate(`/teacher/${announcement.teacher_id}/gallery`);
    }
  };

  const handlePrevAttachment = (e) => {
    e.stopPropagation();
    setCurrentAttachmentIndex(prev => 
      prev === 0 ? allAttachments.length - 1 : prev - 1
    );
  };

  const handleNextAttachment = (e) => {
    e.stopPropagation();
    setCurrentAttachmentIndex(prev => 
      prev === allAttachments.length - 1 ? 0 : prev + 1
    );
  };

  // Determine if the card should be clickable
  const isClickable = announcement.link_url || (hasImageAttachments && !hasPdfAttachments && announcement.teacher_id);

  return (
    <div
      onClick={handleClick}
      className={`flex-shrink-0 w-[300px] rounded-xl overflow-hidden shadow-md transition-transform hover:scale-[1.02] ${
        isClickable ? 'cursor-pointer' : ''
      }`}
      style={{ 
        scrollSnapAlign: 'start',
        backgroundColor: 'white',
        border: '1px solid #E3E1DB'
      }}
    >
      {/* Attachment Display */}
      {allAttachments.length > 0 && (
        <div className="w-full h-56 overflow-hidden relative">
          <AttachmentRenderer attachment={allAttachments[currentAttachmentIndex]} />
          
          {/* Attachment navigation for multiple attachments */}
          {allAttachments.length > 1 && (
            <>
              <button
                onClick={handlePrevAttachment}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={handleNextAttachment}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              
              {/* Dots indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {allAttachments.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentAttachmentIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Content */}
      <div className="p-2">
        <h3 
          className="font-semibold text-lg mb-2 line-clamp-2"
          style={{ color: '#163329', fontFamily: 'Spectral, serif' }}
        >
          {announcement.title}
        </h3>
        
        {announcement.description && (
          <p 
            className="text-sm line-clamp-2 mb-3"
            style={{ color: '#5C635A' }}
          >
            {announcement.description}
          </p>
        )}
        
        {/* Teacher info */}
        <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
            style={{ backgroundColor: '#163329' }}
          >
            {announcement.teacher_name?.charAt(0)?.toUpperCase() || 'T'}
          </div>
          <span className="text-sm" style={{ color: '#5C635A' }}>
            {announcement.teacher_name || 'Teacher'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementRibbon;
